import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { onboardingService, STEP_CONFIG, type OnboardingProgress, type AccountType } from '@/lib/api/services/onboarding'
import { AccountTypeStep } from '@/components/onboarding/AccountTypeStep'
import { UserProfileStep } from '@/components/onboarding/UserProfileStep'
import { WorkspaceSetupStep } from '@/components/onboarding/WorkspaceSetupStep'
import { SubscriptionStep } from '@/components/onboarding/SubscriptionStep'
import { CompletionStep } from '@/components/onboarding/CompletionStep'
import { useToast } from '@/components/ui/use-toast'

export default function OnboardingWizard() {
  const [progress, setProgress] = useState<OnboardingProgress | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const navigate = useNavigate()
  const { toast } = useToast()

  // Load existing progress on mount
  useEffect(() => {
    loadProgress()
  }, [])

  const loadProgress = async () => {
    try {
      setLoading(true)
      const existingProgress = await onboardingService.getProgress()

      if (existingProgress?.status === 'Completed' || existingProgress?.status === 'Skipped') {
        // Already completed, redirect to dashboard
        navigate('/dashboard')
        return
      }

      setProgress(existingProgress)
    } catch (error) {
      console.error('Failed to load onboarding progress:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleStart = async (accountType: AccountType) => {
    try {
      setSubmitting(true)
      const newProgress = await onboardingService.start(accountType)
      setProgress(newProgress)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to start onboarding. Please try again.',
        variant: 'destructive'
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleCompleteStep = async (data: Record<string, unknown>) => {
    if (!progress) return

    try {
      setSubmitting(true)
      const updatedProgress = await onboardingService.completeStep({
        stepName: progress.currentStep,
        data
      })
      setProgress(updatedProgress)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to complete step. Please try again.',
        variant: 'destructive'
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleComplete = async () => {
    try {
      setSubmitting(true)
      const result = await onboardingService.complete()

      if (result.warnings.length > 0) {
        toast({
          title: 'Onboarding completed with warnings',
          description: result.warnings.join(', ')
        })
      } else {
        toast({
          title: 'Welcome to Templatean!',
          description: 'Your workspace is ready.'
        })
      }

      navigate('/dashboard')
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to complete onboarding. Please try again.',
        variant: 'destructive'
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleSkip = async () => {
    try {
      setSubmitting(true)
      await onboardingService.skip(true)

      toast({
        title: 'Onboarding skipped',
        description: 'Default settings have been applied. You can customize them in Settings.'
      })

      navigate('/dashboard')
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to skip onboarding. Please try again.',
        variant: 'destructive'
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  // Get current step config
  const currentStepIndex = progress ? progress.currentStepNumber - 1 : 0
  const currentConfig = STEP_CONFIG[currentStepIndex] ?? STEP_CONFIG[0]!
  const progressPercent = progress?.completionPercentage ?? 0

  const renderStep = () => {
    if (!progress) {
      // First time - show account type selection
      return (
        <AccountTypeStep
          onSelect={handleStart}
          disabled={submitting}
        />
      )
    }

    switch (progress.currentStep) {
      case 'account-type':
        return (
          <AccountTypeStep
            onSelect={(type) => handleCompleteStep({ accountType: type })}
            selectedType={progress.accountType}
            disabled={submitting}
          />
        )
      case 'user-profile':
        return (
          <UserProfileStep
            onSubmit={handleCompleteStep}
            disabled={submitting}
          />
        )
      case 'workspace-setup':
        return (
          <WorkspaceSetupStep
            onSubmit={handleCompleteStep}
            disabled={submitting}
          />
        )
      case 'subscription':
        return (
          <SubscriptionStep
            onSubmit={handleCompleteStep}
            disabled={submitting}
          />
        )
      case 'completion':
        return (
          <CompletionStep
            progress={progress}
            onComplete={handleComplete}
            disabled={submitting}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Welcome to Templatean
          </h1>
          <p className="text-slate-600">
            Let's get your workspace set up in just a few steps
          </p>
        </div>

        {/* Progress indicator */}
        {progress && (
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-slate-700">
                Step {progress.currentStepNumber} of {progress.totalSteps}: {currentConfig.title}
              </span>
              <span className="text-sm text-slate-500">
                {progressPercent}% complete
              </span>
            </div>
            <Progress value={progressPercent} className="h-2" />
            <div className="flex justify-between mt-2">
              {STEP_CONFIG.map((step, index) => (
                <div
                  key={step.name}
                  className={`flex-1 text-center ${
                    index < currentStepIndex
                      ? 'text-primary'
                      : index === currentStepIndex
                      ? 'text-slate-900 font-medium'
                      : 'text-slate-400'
                  }`}
                >
                  <div className="text-xs hidden sm:block">{step.title}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step content */}
        <Card className="shadow-lg">
          <CardContent className="p-8">
            {renderStep()}
          </CardContent>
        </Card>

        {/* Skip button */}
        {progress && progress.currentStep !== 'completion' && (
          <div className="text-center mt-6">
            <Button
              variant="ghost"
              onClick={handleSkip}
              disabled={submitting}
              className="text-slate-500 hover:text-slate-700"
            >
              Skip setup and use defaults
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
